<script lang="ts">
  import { tick } from 'svelte';
  let { onchange }: { onchange: (blob: Blob | null) => void } = $props();

  let canvas = $state<HTMLCanvasElement>();
  let fileInput: HTMLInputElement;
  let source = $state<ImageBitmap | null>(null);
  let filename = $state('');
  let zoom = $state(1);
  let offsetX = 0;
  let offsetY = 0;
  let dragging = false;
  let dragX = 0;
  let dragY = 0;
  let renderTimer: number | null = null;

  async function choose(event: Event) {
    const file = (event.currentTarget as HTMLInputElement).files?.[0];
    if (!file) return;
    source?.close();
    source = await createImageBitmap(file);
    filename = file.name;
    zoom = 1;
    offsetX = 0;
    offsetY = 0;
    await tick();
    render();
  }

  function render() {
    if (!source || !canvas) return;
    const currentCanvas = canvas;
    const context = currentCanvas.getContext('2d');
    if (!context) return;
    const size = currentCanvas.width;
    const baseScale = Math.max(size / source.width, size / source.height);
    const scale = baseScale * zoom;
    const width = source.width * scale;
    const height = source.height * scale;
    const maxX = Math.max(0, (width - size) / 2);
    const maxY = Math.max(0, (height - size) / 2);
    offsetX = Math.max(-maxX, Math.min(maxX, offsetX));
    offsetY = Math.max(-maxY, Math.min(maxY, offsetY));
    context.clearRect(0, 0, size, size);
    context.drawImage(source, (size - width) / 2 + offsetX, (size - height) / 2 + offsetY, width, height);
    if (renderTimer !== null) window.clearTimeout(renderTimer);
    renderTimer = window.setTimeout(() => currentCanvas.toBlob((blob) => onchange(blob), 'image/png'), 90);
  }

  function pointerDown(event: PointerEvent) {
    if (!source || !canvas) return;
    dragging = true;
    dragX = event.clientX;
    dragY = event.clientY;
    canvas.setPointerCapture(event.pointerId);
  }

  function pointerMove(event: PointerEvent) {
    if (!dragging || !canvas) return;
    const ratio = canvas.width / canvas.getBoundingClientRect().width;
    offsetX += (event.clientX - dragX) * ratio;
    offsetY += (event.clientY - dragY) * ratio;
    dragX = event.clientX;
    dragY = event.clientY;
    render();
  }

  function clear() {
    source?.close();
    source = null;
    filename = '';
    fileInput.value = '';
    onchange(null);
  }

  function nudge(event: KeyboardEvent) {
    if (!source || !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
    event.preventDefault();
    const distance = event.shiftKey ? 32 : 8;
    if (event.key === 'ArrowLeft') offsetX -= distance;
    if (event.key === 'ArrowRight') offsetX += distance;
    if (event.key === 'ArrowUp') offsetY -= distance;
    if (event.key === 'ArrowDown') offsetY += distance;
    render();
  }
</script>

<div class:has-image={source} class="cropper">
  {#if source}
    <canvas bind:this={canvas} width="1024" height="1024" tabindex="0" onkeydown={nudge} onpointerdown={pointerDown} onpointermove={pointerMove} onpointerup={() => dragging = false} onpointercancel={() => dragging = false} aria-label="Badge artwork crop. Drag or use arrow keys to reposition."></canvas>
    <div class="crop-controls">
      <label for="zoom">Zoom</label>
      <input id="zoom" type="range" min="1" max="2.5" step="0.01" bind:value={zoom} oninput={render} />
      <button type="button" onclick={clear}>Remove</button>
    </div>
    <p title={filename}>{filename}</p>
  {:else}
    <button class="file-prompt" type="button" onclick={() => fileInput.click()}>
      <svg viewBox="0 0 64 64" aria-hidden="true"><path d="M32 12v31M19 25l13-13 13 13M12 44v8h40v-8" /></svg>
      <strong>Choose badge artwork</strong>
      <span>PNG, JPEG, or WebP · square crop included</span>
    </button>
  {/if}
  <input bind:this={fileInput} class="visually-hidden" type="file" accept="image/png,image/jpeg,image/webp" onchange={choose} />
</div>

<style>
  .cropper { position: relative; display: grid; min-height: 300px; place-items: center; border: 1px dashed var(--line-strong); background: color-mix(in srgb, var(--surface-soft) 80%, transparent); }
  .cropper.has-image { display: block; padding: 14px; }
  canvas { display: block; width: 100%; aspect-ratio: 1; object-fit: cover; cursor: grab; touch-action: none; }
  canvas:active { cursor: grabbing; }
  canvas:focus-visible { outline: 3px solid color-mix(in srgb, var(--aqua) 55%, transparent); outline-offset: 3px; }
  .file-prompt { display: grid; justify-items: center; gap: 8px; width: 100%; min-height: 300px; padding: 28px; border: 0; background: transparent; color: var(--ink); cursor: pointer; }
  .file-prompt svg { width: 48px; fill: none; stroke: var(--teal); stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; transition: transform 220ms ease; }
  .file-prompt strong { margin-top: 8px; font-family: var(--display); font-size: 21px; font-weight: 500; }
  .file-prompt span { color: var(--muted); font-size: 13px; }
  .crop-controls { display: grid; grid-template-columns: auto 1fr auto; align-items: center; gap: 11px; margin-top: 13px; }
  .crop-controls label { color: var(--muted); font-size: 11px; font-weight: 800; letter-spacing: .08em; text-transform: uppercase; }
  .crop-controls input { width: 100%; accent-color: var(--violet); }
  .crop-controls button { min-height: 40px; border: 0; background: transparent; color: var(--coral-dark); font-size: 12px; font-weight: 750; cursor: pointer; }
  p { max-width: 100%; margin: 8px 0 0; overflow: hidden; color: var(--muted); font-size: 11px; text-overflow: ellipsis; white-space: nowrap; }
  .visually-hidden { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); clip-path: inset(50%); white-space: nowrap; }
  @media (hover: hover) { .file-prompt:hover svg { transform: translateY(-5px); } }
  @media (max-width: 600px) { .cropper, .file-prompt { min-height: 250px; } }
</style>
